import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getTemplate, TEMPLATES } from '@/data/templates';
import * as Icons from 'lucide-react';
import { motion } from 'framer-motion';
import TemplateCard from '@/components/TemplateCard';

const SITE_NAME = 'Everything PDF';
const PROD_HOST = 'https://pdf-forms-hub.emergent.host';

function defaultFaqs(template) {
  return [
    {
      q: `Is the ${template.title} template really free?`,
      a: `Yes. You can generate and download a ${template.title} as many times as you like, no signup required. Free downloads include a small ${SITE_NAME} watermark — upgrade to Pro to remove it.`,
    },
    {
      q: `How long does it take to fill out the ${template.title}?`,
      a: `Most users finish a ${template.title} in under 2 minutes. Fill the form on the left, watch the live preview update on the right, then click Download PDF.`,
    },
    {
      q: `Can I use AI to draft the ${template.title} for me?`,
      a: `Yes — Pro users can describe what they need in plain English ("Mutual NDA between Acme and a contractor, California law, 3-year term") and Claude AI fills out the entire form automatically.`,
    },
    {
      q: `Is this ${template.title} legally binding?`,
      a: `Templates on ${SITE_NAME} are general-purpose drafts based on common practice. For high-stakes situations or unusual jurisdictions, have a licensed attorney review the document before signing.`,
    },
    {
      q: `Do I need to install any software?`,
      a: `No. ${SITE_NAME} runs entirely in your browser — your filled documents are generated client-side and never uploaded to our servers. It works on desktop and mobile.`,
    },
  ];
}

export default function SEOLanding({ templateId }) {
  const template = getTemplate(templateId);
  const navigate = useNavigate();

  if (!template) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <h1 className="font-display text-3xl font-bold">Template not found</h1>
        <Link to="/" className="mt-6 underline text-orange-600 inline-block">Back to home</Link>
      </div>
    );
  }

  const Icon = Icons[template.icon] || Icons.FileText;
  const path = `/free-${template.id}-template`;
  const fullUrl = `${PROD_HOST}${path}`;
  const title = `Free ${template.title} Template (2026) — PDF Download`;
  const description = `${template.description} Generate, customize, and download a free ${template.title} PDF in 60 seconds. No signup required. AI-assisted drafting available.`;
  const related = TEMPLATES.filter(t => t.category === template.category && t.id !== template.id).slice(0, 3);
  const faqs = defaultFaqs(template);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: `How to generate a free ${template.title}`,
    step: [
      { '@type': 'HowToStep', name: 'Open the editor', text: `Click "Generate Yours Free" to open the ${template.title} editor.` },
      { '@type': 'HowToStep', name: 'Fill the form', text: 'Fill in your details on the left. The live preview updates as you type.' },
      { '@type': 'HowToStep', name: 'Download the PDF', text: 'Click Download PDF — your document is generated instantly in your browser.' },
    ],
  };

  return (
    <div data-testid={`seo-${template.id}`}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={fullUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={fullUrl} />
        <meta property="og:image" content={`${PROD_HOST}/og-default.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={`${PROD_HOST}/og-default.png`} />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
      </Helmet>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 -left-24 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
          <div className="absolute top-40 right-0 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl" />
        </div>
        <div className="max-w-5xl mx-auto px-6 pt-16 pb-12 relative">
          <Link to="/" className="text-sm text-stone-500 hover:text-orange-600 inline-flex items-center gap-1 mb-8" data-testid="seo-back">
            <Icons.ArrowLeft size={14} /> All templates
          </Link>

          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>
            <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-800 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider mb-5">
              <Icon size={14} /> Free {template.category.replace('-', ' ')} template
            </div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95]">
              Free <span className="text-orange-600 italic font-serif font-medium">{template.title}</span><br />
              Template
            </h1>
            <p className="mt-6 text-lg text-stone-600 leading-relaxed max-w-2xl">
              {template.description} Customize, preview live, and download as a polished PDF — in your browser, in under a minute.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate(`/editor/${template.id}`)}
                data-testid="seo-cta-primary"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-orange-600 hover:bg-orange-700 text-white text-base font-medium btn-clay"
              >
                <Icons.Sparkles size={18} /> Generate Yours Free
              </button>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-stone-300 text-base font-medium hover:bg-stone-100"
              >
                See Pro features
              </Link>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-stone-500">
              <span className="flex items-center gap-1.5"><Icons.Check size={15} className="text-emerald-600" /> No signup</span>
              <span className="flex items-center gap-1.5"><Icons.Check size={15} className="text-emerald-600" /> Live preview</span>
              <span className="flex items-center gap-1.5"><Icons.Check size={15} className="text-emerald-600" /> AI-assisted drafting</span>
              <span className="flex items-center gap-1.5"><Icons.Check size={15} className="text-emerald-600" /> Browser-only — your data stays with you</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHAT'S IN IT */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.18em] text-stone-500 mb-3">What's included</div>
            <h2 className="font-display font-bold text-3xl tracking-tight mb-4">A complete {template.title.toLowerCase()}, ready to sign.</h2>
            <p className="text-stone-600 leading-relaxed">
              Our {template.title} template includes every field you need — pre-written boilerplate, sensible defaults,
              and clean professional formatting. Fill what's relevant, skip what isn't.
            </p>
          </div>
          <div className="lg:col-span-7">
            <div className="bg-white border border-stone-200 rounded-3xl p-6">
              <div className="text-xs uppercase tracking-wider text-stone-500 mb-3">Fields you'll fill in</div>
              <ul className="space-y-2">
                {template.fields.slice(0, 12).map(f => (
                  <li key={f.key} className="flex items-start gap-2.5 text-sm">
                    <Icons.Check size={15} className="text-orange-600 shrink-0 mt-0.5" />
                    <span className="text-stone-700">
                      <strong>{f.label}</strong>
                      {f.placeholder && <span className="text-stone-500"> — e.g. "{f.placeholder.split('\n')[0].slice(0, 60)}{f.placeholder.length > 60 ? '…' : ''}"</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <div className="text-xs uppercase tracking-[0.18em] text-stone-500 mb-2">How it works</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">From blank to signed in 3 steps.</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { n: '01', title: 'Open the editor', desc: `Click "Generate Yours Free" to load the ${template.title} editor.`, icon: 'MousePointer2' },
            { n: '02', title: 'Fill the form', desc: 'Type into the form on the left. The PDF preview on the right updates instantly as you go.', icon: 'PencilLine' },
            { n: '03', title: 'Download the PDF', desc: 'One click and your document is ready. The PDF is generated locally — your data never leaves your browser.', icon: 'Download' },
          ].map(step => {
            const S = Icons[step.icon] || Icons.Circle;
            return (
              <div key={step.n} className="bg-white border border-stone-200 rounded-2xl p-6">
                <div className="text-xs font-mono text-stone-400 mb-3">{step.n}</div>
                <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-700 grid place-items-center mb-3">
                  <S size={18} />
                </div>
                <h3 className="font-display font-bold tracking-tight text-lg mb-1.5">{step.title}</h3>
                <p className="text-sm text-stone-600 leading-relaxed">{step.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA BAND */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <div className="relative overflow-hidden bg-stone-900 text-white rounded-3xl p-8 sm:p-12 text-center">
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-orange-500/20 blur-3xl rounded-full" />
          <div className="relative">
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">Ready to generate your {template.title}?</h2>
            <p className="text-stone-300 mt-3 max-w-xl mx-auto">Fully customizable, free to download, no email asked.</p>
            <button
              onClick={() => navigate(`/editor/${template.id}`)}
              data-testid="seo-cta-secondary"
              className="inline-flex items-center gap-2 mt-7 bg-orange-600 hover:bg-orange-700 text-white rounded-full px-7 py-4 text-base font-medium btn-clay"
            >
              <Icons.Sparkles size={16} /> Open the {template.title} editor
            </button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <div className="text-xs uppercase tracking-[0.18em] text-stone-500 mb-2">FAQ</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">Common questions</h2>
        </div>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <details key={i} className="group bg-white border border-stone-200 rounded-2xl px-5 py-4 [&[open]>summary>svg]:rotate-180">
              <summary className="flex items-center justify-between gap-3 cursor-pointer list-none">
                <span className="font-display font-bold text-stone-900 tracking-tight">{f.q}</span>
                <Icons.ChevronDown size={18} className="text-stone-400 transition-transform shrink-0" />
              </summary>
              <p className="text-sm text-stone-600 leading-relaxed mt-3">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 py-12">
          <div className="mb-6">
            <div className="text-xs uppercase tracking-[0.18em] text-stone-500 mb-2">More from {template.category.replace('-', ' ')}</div>
            <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">You might also need</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {related.map(t => <TemplateCard key={t.id} template={t} />)}
          </div>
        </section>
      )}
    </div>
  );
}
